import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { mapTemporaryToDto, type TemporaryContract } from '@/store/contracts/contracts.mappers';
import type { InviteEvent } from '@/store/invites/invites.types';

import type {
  ConfirmInviteData,
  InviteDetailsDto,
  InviteEmailTemplate,
  LoginRequest,
  LoginResponse,
  RegistrationConfirmationResponse,
  TwoFactorVerifyResponse,
} from './auth.dto';
import { mapInviteDetails } from './auth.mappers';

/** Code-based 2FA channels — a one-time code is sent and verified. */
export type TwoFactorChallengeMethod = 'email' | 'sms' | 'call';
/** Every selectable 2FA method, including the authenticator-app (TOTP) flow. */
export type TwoFactorMethod = TwoFactorChallengeMethod | 'totp';

// ── Auth account (the login side of the user, separate from the people profile) ──

/**
 * The auth-service record behind the session. Its `phone` is the number every SMS
 * (and voice) 2FA code goes to: auth fills that column from the account *login*
 * only, so a `phone` contact written to the people service never reaches it.
 */
export type AuthAccount = {
  id: string;
  email: string | null;
  phone: string | null;
};

/** Auth serialises an unset login half as the string "false" (or ""), not null. */
const loginOrNull = (value: string | null | undefined) =>
  value && value !== 'false' ? value : null;

const toAuthAccount = (raw: AuthAccount): AuthAccount => ({
  ...raw,
  email: loginOrNull(raw.email),
  phone: loginOrNull(raw.phone),
});

export const getAuthAccount = async () => {
  const res = await getRequest<AuthAccount>(API.AUTH, '/user/me');
  return toAuthAccount(res.data);
};

/**
 * Step one of adding — or replacing — one half of the account login: an E.164 phone
 * number, or an email address. This is the only way to give a code-based 2FA method a
 * channel to reach, since auth sends to `phone`/`email` on this record and fills them
 * from the login alone. Nothing changes yet: auth texts or emails a 6-digit code to the
 * *new* login and answers with a plain-text token for {@link confirmLoginChange}. The
 * value must not already belong to another account, nor be the current one.
 */
export const requestLoginChange = async (userId: string, login: string) => {
  const res = await putRequest<string, { login: string }>(API.AUTH, `/user/${userId}`, {
    login,
  });
  return String(res.data).trim();
};

/**
 * Step two: applies the change with the token from {@link requestLoginChange} and the
 * code sent to the new login. The other half of the login is left as it is. A token is
 * single-use — a wrong code burns it, so a retry needs a fresh request.
 */
export const confirmLoginChange = async (userId: string, secret: string, code: string) => {
  const res = await patchRequest<AuthAccount, { secret: string; code: string }>(
    API.AUTH,
    `/user/confirm-update/${userId}`,
    { secret, code },
  );
  return toAuthAccount(res.data);
};

/**
 * Checks a password against the account by signing in with it. `/user/update-password`
 * takes the new password alone, so this is the only way to prove the current one first;
 * a wrong one counts against the same attempt budget as the sign-in screen. Whatever
 * session it opens is thrown away by the revocation {@link updatePassword} does.
 */
export const checkPassword = async (login: string, password: string) => {
  const res = await postRequest<LoginResponse, LoginRequest>(API.AUTH, '/login', {
    login: login.toLowerCase(),
    password,
  });
  return res.data;
};

/**
 * Resolves when sign-up can go ahead with the login and throws the auth `DUPLICATE` error
 * when an account already holds it. Lower-cased to match how /register stores it.
 */
export const checkLogin = async (login: string) => {
  await postRequest<void, { login: string }>(API.AUTH, '/check-login', {
    login: login.toLowerCase(),
  });
};

/** Sets a new password and revokes every session of the account, this one included. */
export const updatePassword = async (password: string) => {
  await patchRequest<void, { password: string }>(API.AUTH, '/user/update-password', {
    password,
  });
};

export const hardDeleteUser = async (userId: string) => {
  await deleteRequest(API.AUTH, `user/remove/${userId}`);
};

export const inviteUser = async (
  contact: string,
  name: string,
  role: string,
  serviceId: string,
  serviceType: string,
  serviceName?: string,
  roleName?: string,
  event?: InviteEvent,
  message?: string,
  temporary?: TemporaryContract,
  /** Guests the invitee brings along; event invitations only. */
  additionalGuests?: number,
  emailTemplate?: InviteEmailTemplate,
) => {
  await postRequest(API.AUTH, '/user/invite', {
    contact: contact.toLowerCase(),
    name,
    role,
    service_id: serviceId,
    service_type: serviceType,
    service_name: serviceName,
    role_name: roleName,
    event,
    message: message || undefined,
    temporary: mapTemporaryToDto(temporary),
    additional_guests: additionalGuests,
    email_template: emailTemplate,
  });
};

export const inviteUsersBulk = async (
  /** `additionalGuests`: the guests each invitee brings along; event invitations only. */
  invitees: { contact: string; name: string; additionalGuests?: number }[],
  role: string,
  serviceId: string,
  serviceType: string,
  serviceName?: string,
  event?: InviteEvent,
  message?: string,
  /** One contract and permission set for the whole batch. */
  temporary?: TemporaryContract,
  emailTemplate?: InviteEmailTemplate,
) => {
  await postRequest(API.AUTH, '/user/invite/bulk', {
    invitees: invitees.map(({ contact, name, additionalGuests }) => ({
      contact: contact.toLowerCase(),
      name,
      additional_guests: additionalGuests,
    })),
    role,
    service_id: serviceId,
    service_type: serviceType,
    service_name: serviceName,
    event,
    message: message || undefined,
    temporary: mapTemporaryToDto(temporary),
    email_template: emailTemplate,
  });
};

// Resolves with what the sign-up form is prefilled with while the invitation can still be
// accepted; does not use the secret up.
export const checkInvite = async (secret: string) => {
  const res = await postRequest<InviteDetailsDto, { secret: string }>(
    API.AUTH,
    '/user/check-invite',
    { secret },
  );
  return mapInviteDetails(res.data);
};

export const confirmInvite = async (data: ConfirmInviteData) => {
  return await postRequest<RegistrationConfirmationResponse, unknown>(
    API.AUTH,
    '/user/confirm-invite',
    { ...data, provider: 'basic' },
  );
};

export const sendMagicLink = async (login: string) => {
  await postRequest(API.AUTH, `/magic-link?login=${encodeURIComponent(login)}`, null);
};

export const confirmMagicLink = async (secret: string) => {
  return await postRequest<LoginResponse, { secret: string }>(API.AUTH, '/confirm-magic-link', {
    secret,
  });
};

// ── 2FA management (authenticated; require a Bearer token) ──────────────────

/**
 * Turn on a code-based 2FA method for the current user. TOTP is enabled via its
 * own QR endpoint (see `enableTotp` in the store), not here.
 */
export const enableTwoFactorMethod = async (method: TwoFactorChallengeMethod) => {
  await postRequest(API.AUTH, `/2fa/enable/${method}`, {});
};

export const disableTwoFactorMethod = async (method: TwoFactorMethod) => {
  await postRequest(API.AUTH, `/2fa/disable/${method}`, {});
};

/**
 * Confirm a freshly-enabled 2FA method. Enabling now only arms a method — it stays
 * pending until the user proves possession: the code from their authenticator app
 * (TOTP) or the one-time code sent to their email/phone. Recovery codes are returned
 * once, on the first method the account ever confirms; an empty array otherwise.
 */
export const confirmTwoFactorMethod = async (
  method: TwoFactorMethod,
  code: string,
): Promise<string[]> => {
  const res = await postRequest<{ recovery_codes?: string[] }, { method: string; code: string }>(
    API.AUTH,
    `/2fa/confirm/${method}`,
    { method, code },
  );
  return res.data?.recovery_codes ?? [];
};

export const setDefaultTwoFactorMethod = async (method: TwoFactorMethod) => {
  await postRequest(API.AUTH, `/2fa/default/${method}`, {});
};

// ── 2FA login challenge (unauthenticated; carry the token from /login) ───────

/**
 * Send a one-time code to the chosen channel during a login challenge.
 * `token` is the `two_factor_token` returned by /login, /oauth/.../complete or
 * /confirm-magic-link.
 */
export const sendTwoFactorCode = async (method: TwoFactorChallengeMethod, token: string) => {
  await postRequest<void, { method: string; token: string }>(API.AUTH, `/2fa/send/${method}`, {
    method,
    token,
  });
};

/** Verify a code-based 2FA challenge and exchange it for auth tokens. */
export const verifyTwoFactorCode = async (
  method: TwoFactorChallengeMethod,
  token: string,
  code: string,
) =>
  postRequest<TwoFactorVerifyResponse, { method: string; token: string; code: string }>(
    API.AUTH,
    `/2fa/verify/${method}`,
    { method, token, code },
  );

/**
 * Spend a recovery code to bypass a lost 2FA device. On success the backend wipes
 * ALL 2FA methods (totp/sms/call/email/passkey) and returns a fresh token pair.
 * `token` is the two_factor_token from the login challenge.
 */
export const verifyRecoveryCode = async (token: string, code: string) =>
  postRequest<TwoFactorVerifyResponse, { token: string; code: string }>(API.AUTH, '/2fa/recovery', {
    token,
    code,
  });
